/**
 * Landing page for the Stripe report payment links. Each link is configured (in
 * Stripe) to redirect here after payment with `?session_id=cs_...`. We pull-verify
 * the session via the backend (the authoritative paid/unpaid check — no webhook),
 * then hand off to Studio with the report unlocked. The link carries
 * client_reference_id="<projectId>:<reportType>" so we know which project + tier
 * to deliver. Mirrors the export wizard's server re-verification pattern.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export function StudioCheckoutReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const [message, setMessage] = useState("Confirming your payment…");

  useEffect(() => {
    let cancelled = false;
    const sessionId = params.get("session_id");
    const fail = (msg: string) => {
      if (cancelled) return;
      setFailed(true);
      setMessage(msg);
    };
    (async () => {
      if (!sessionId) return fail("Missing checkout session.");
      try {
        const res = await fetch(
          `/api/stripe/checkout-session-status?id=${encodeURIComponent(sessionId)}`,
          { credentials: "include" }
        );
        if (!res.ok) throw new Error(`verify failed (${res.status})`);
        const data = (await res.json()) as {
          paymentStatus: string;
          clientReferenceId: string;
        };
        if (cancelled) return;
        if (data.paymentStatus !== "paid") {
          return fail("Your payment hasn't completed yet. If you were charged, contact support.");
        }
        const parts = (data.clientReferenceId || "").split(":");
        // OG-app Summary PDF: "summary:<projectId>" → back to the canvas, which
        // regenerates + downloads the report on ?paidReport=1.
        if (parts[0] === "summary" && parts[1]) {
          navigate(`/projects/${parts[1]}/canvas?paidReport=1`, { replace: true });
          return;
        }
        // Studio report tiers: "<projectId>:<reportType>".
        const [projectId, reportType] = parts;
        if (!projectId || (reportType !== "construction" && reportType !== "financial")) {
          return fail("Payment confirmed, but we couldn't match it to a project.");
        }
        navigate(`/projects/${projectId}/studio?paidReport=${reportType}`, { replace: true });
      } catch (err) {
        fail(err instanceof Error ? err.message : "Couldn't confirm your payment.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params, navigate]);

  return (
    <div className="w-screen h-screen grid place-items-center bg-[#f9fafb]">
      <div className="max-w-sm px-6 text-center">
        <p className="text-[15px] font-semibold text-[#111827]">
          {failed ? "Payment" : "One moment"}
        </p>
        <p className="mt-2 text-[13px] text-[#6b7280]">{message}</p>
        {failed && (
          <a
            href="/home"
            className="mt-4 inline-block text-[13px] font-semibold text-[#111827] underline"
          >
            Back to projects
          </a>
        )}
      </div>
    </div>
  );
}
